// Video Director — the separate L&D video path (project.flow === "ld"). Everything here is
// rendered only for that flow; freestyle projects never reach it. Mirrors aidea-middleware
// video-director.types.ts (LD_DEFAULT_STYLE, DRAFT_MODEL, estimateLd).
import React, { useEffect, useState } from "react";
import i18n from "i18next";
import { LuClapperboard, LuGraduationCap, LuLightbulb, LuListChecks, LuPackage, LuShieldAlert, LuTheater } from "react-icons/lu";
import { getToken } from "../../../../../common/environment_config.es6";
import { BriefModelRow, ModelPick } from "./DirectorPanels";

const t = (key, opts) => i18n.t(`FileModal.APIProviders.VideoGeneration.Director.${key}`, opts);
const tl = (key, opts) => t(`Ld.${key}`, opts);

const TAKE_SEC = 8; // server TAKE_SEC
const CAST_MAX = 4; // server LIMITS.cast
const LOCATIONS_MAX = 4; // server LIMITS.locations
/** Cheapest priced Director video model (the stage price list has no veo-lite row). */
export const DRAFT_MODEL = "veo-fast";
/** Finalize default: the brief's pick when priced and not the draft model; else the first priced non-draft model (veo preferred); none qualifies -> the draft model. */
export const defaultFinalModel = (models, briefPick) => {
  if (briefPick && briefPick !== DRAFT_MODEL && typeof models?.video?.[briefPick] === "number") return briefPick;
  // Raw caps ids, not modelIds(): the picker may hide the duplicate Omni "veo" row, which is still the finalize default.
  const ids = models?.videoCaps?.map((c) => c.id) ?? ["veo-fast", "veo"];
  const ok = ids.filter((id) => id !== DRAFT_MODEL && typeof models?.video?.[id] === "number");
  return ok.includes("veo") ? "veo" : (ok[0] ?? DRAFT_MODEL);
};
export const LD_DEFAULT_STYLE = {
  safety: "industrial-safety",
  process: "corporate-clean",
  scenario: "corporate-clean",
  concept: "flat-motion-graphics",
  product: "corporate-clean",
};
const USE_CASES = [
  ["safety", LuShieldAlert],
  ["process", LuListChecks],
  ["scenario", LuTheater],
  ["concept", LuLightbulb],
  ["product", LuPackage],
];
const labelStyle = { fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px" };

/** Server estimateLd: shots = ceil(sec / 8); low = shots × (take + image); high adds the cast + location stills. */
export function estimateLd(brief, imageUnit, takeUnit, maxShots) {
  const sec = Number(brief.targetSec) || 0;
  const shots = Math.min(maxShots, Math.max(1, Math.ceil(sec / TAKE_SEC)));
  const castMode = brief.castMode ?? (brief.ld?.useCase === "concept" ? "none" : "ai");
  const cast = castMode === "none" ? 0 : castMode === "photos" ? (brief.castPhotos?.length ?? 0) : CAST_MAX;
  const stills = cast + LOCATIONS_MAX;
  const low = shots * (takeUnit + imageUnit);
  return { shots, stills, low, high: low + stills * imageUnit };
}

/** Draft first: the author's choice, else on when the video needs more than 4 shots. */
export const ldDraftOn = (brief) =>
  brief?.ld?.draftFirst ?? Math.ceil((Number(brief?.targetSec) || 0) / TAKE_SEC) > 4;

/** Shots whose clip on the timeline is a finished draft with nothing in flight. */
export const draftShotIds = (project) =>
  project.shots
    .filter((s) => {
      const seg = project.edl.find((x) => x.shotId === s.id);
      const tk = seg && s.takes.find((x) => x.id === seg.takeId);
      return !!tk?.draft && tk.status === "ready" && !s.takes.some((x) => x.status === "pending");
    })
    .map((s) => s.id);

const priceOf = (credit, model) => Number(credit?.models?.video?.[model]) || 0;

/**
 * The three AI start paths, shared by the empty states. The two scene paths need narration to read
 * (disabled with a tooltip otherwise); "your own prompt" never does. One primary, the rest secondary.
 */
export function StartOptions({ busy, hasNarration, onCinematic, onLd, onPrompt }) {
  const primary = hasNarration ? "cinematic" : "prompt";
  const option = (id, Icon, label, onClick, needsScene) => {
    const off = busy || (needsScene && !hasNarration);
    return (
      <span key={id} title={needsScene && !hasNarration ? t("NeedsNarration") : undefined}>
        <button
          type="button"
          className={`add-video-modal__btn add-video-modal__btn--${primary === id ? "primary" : "secondary"}`}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
          disabled={off}
          onClick={onClick}
        >
          <Icon size={14} aria-hidden="true" />
          {label}
          {needsScene && <span style={{ opacity: 0.7, fontWeight: 400 }}>{` · ${t("AnalysisCost")}`}</span>}
        </button>
      </span>
    );
  };
  return (
    <div className="add-video-modal__vd-empty-actions">
      {option("cinematic", LuClapperboard, t("StartCinematic"), onCinematic, true)}
      {option("ld", LuGraduationCap, tl("StartLd"), onLd, true)}
      {option("prompt", LuLightbulb, t("StartOwnPrompt"), onPrompt, false)}
    </div>
  );
}

/** Top of the L&D brief: the objective, the five use cases, audience, length. */
export function LdTop({ d, set, issues, Field }) {
  const ld = d.ld;
  const setLd = (patch) => set({ ld: { ...ld, ...patch } });
  return (
    <>
      <Field
        label={tl("Objective")}
        value={ld.objective}
        multiline
        rows={3}
        maxLength={1000}
        placeholder={tl("ObjectivePlaceholder")}
        missing={issues.includes("ld.objective")}
        required
        onChange={(v) => setLd({ objective: v })}
      />
      <div className="add-video-modal__ai-section" style={{ marginBottom: 12 }}>
        <div className="add-video-modal__ai-label">{tl("UseCase")}</div>
        <div className="add-video-modal__ai-ratio" role="group" aria-label={tl("UseCase")}>
          {USE_CASES.map(([id, Icon]) => (
            <button
              key={id}
              type="button"
              aria-pressed={ld.useCase === id}
              onClick={() => setLd({ useCase: id })}
              className={`add-video-modal__ai-chip${ld.useCase === id ? " add-video-modal__ai-chip--active" : ""}`}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, width: 150 }}
            >
              <Icon size={20} aria-hidden="true" />
              <span style={{ fontWeight: 600 }}>{tl(`UseCases.${id}`)}</span>
              <span style={{ fontSize: 11, fontWeight: 400 }}>{tl(`UseCases.${id}_hint`)}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="add-video-modal__vd-brief-grid">
        <Field
          label={tl("Audience")}
          value={ld.audience}
          maxLength={200}
          onChange={(v) => setLd({ audience: v })}
        />
        <div style={{ marginBottom: 12 }}>
          <div className="add-video-modal__scene-field-label" style={labelStyle}>
            {tl("Length", { sec: d.targetSec })}
          </div>
          <input
            type="range"
            min={10}
            max={120}
            step={5}
            value={d.targetSec}
            aria-label={tl("Length", { sec: d.targetSec })}
            style={{ width: "100%" }}
            onChange={(e) => set({ targetSec: Number(e.target.value) })}
          />
        </div>
      </div>
    </>
  );
}

/** Bottom of the L&D brief: the look row, the cost estimate and the draft-first toggle. */
export function LdBottom({ d, set, credit, videoModel, videoStyles, apiUrl, projectId, lookOpen, onLook }) {
  const [server, setServer] = useState(null); // { maxShots, brandName } from the estimate endpoint
  // The shot limit and the TRAINING org's brand label come from the server (the caller may be another org).
  useEffect(() => {
    let alive = true;
    if (!projectId) return undefined;
    fetch(`${apiUrl}api/video-director/projects/${projectId}/estimate`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => {
        if (alive && res) setServer({ maxShots: res.maxShots, brandName: res.brandName });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [apiUrl, projectId]);
  const brand = !!server?.brandName;
  const draftOn = ldDraftOn(d);
  const pick = d.videoModel ?? (draftOn ? defaultFinalModel(credit?.models) : videoModel); // the FINAL model; drafts stay on DRAFT_MODEL
  const key = d.styleKey || (d.customStyle?.trim() ? null : LD_DEFAULT_STYLE[d.ld.useCase]);
  const style = videoStyles.find((s) => s.styleKey === key);
  const styleName = key ? style?.name || style?.styleKey || key : d.customStyle;
  const est =
    credit && server?.maxShots
      ? estimateLd(
          d,
          Number(credit.imageCredits) || 0,
          priceOf(credit, draftOn ? DRAFT_MODEL : pick),
          server.maxShots
        )
      : null;
  return (
    <>
      <BriefModelRow models={credit?.models} value={pick} onChange={(videoModel) => set({ videoModel })} />
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 13 }}>
        <span style={labelStyle}>{tl("Look")}</span>
        <span>
          {d.styleKey || !brand
            ? d.styleKey
              ? styleName
              : tl("LookDefault", { style: styleName })
            : `${tl("LookBrand")} · ${styleName}`}
        </span>
        <button type="button" className="add-video-modal__btn add-video-modal__btn--ghost" onClick={onLook}>
          {lookOpen ? tl("LookHide") : tl("LookChange")}
        </button>
      </div>
      <div className="add-video-modal__status-card" style={{ marginBottom: 12 }}>
        <div
          className="add-video-modal__status-title"
          title={est ? tl("EstimateDetail", { shots: est.shots, stills: est.stills }) : undefined}
        >
          {est
            ? tl("Estimate", { low: Math.round(est.low), high: Math.round(est.high) })
            : tl("EstimateLoading")}
        </div>
        <label
          title={tl("DraftHint")}
          style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, marginTop: 8 }}
        >
          <input
            type="checkbox"
            checked={draftOn}
            onChange={(e) => set({ ld: { ...d.ld, draftFirst: e.target.checked } })}
          />
          {tl("DraftFirst")}
        </label>
      </div>
    </>
  );
}

/** Edit step: finalize every finished draft on one chosen model. */
export function FinalizeStrip({ count, model, onModel, credit, busy, onRun }) {
  const price = Math.round(count * priceOf(credit, model));
  return (
    <div
      role="region"
      aria-label={tl("Finalize")}
      className="add-video-modal__status-card"
      style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}
    >
      <span style={{ fontSize: 13, fontWeight: 600 }}>{tl("FinalizeStrip", { n: count })}</span>
      <span style={{ fontSize: 13 }}>{tl("FinalizeModel")}</span>
      <ModelPick kind="video" value={model} onChange={onModel} models={credit?.models} />
      <button
        type="button"
        className="app-modal__btn app-modal__btn--primary"
        disabled={busy || count === 0}
        onClick={onRun}
      >
        {tl("FinalizeAll", { n: count, price: t("Models.PriceShort", { n: price }) })}
      </button>
    </div>
  );
}
